import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { UserCog } from 'lucide-react';
import { useStore } from '../../../../hooks/useStore';
import FormPopupFooter from '../../../../components/form-components/common-components/FormPopupFooter';
import UserEditFormFields, { type UserEditFormValues } from './UserEditFormFields';
import styles from './UserForm.module.css';
import { toast } from 'react-toastify';

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
}

const validationSchema = Yup.object({
  role: Yup.string().required('Rol seçiniz'),
});

const EditUserModal = ({ open, onClose }: EditUserModalProps) => {
  const { userManagementStore } = useStore();
  const selectedUser = userManagementStore.selectedUser;
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await userManagementStore.getRoles();
        if (response && response.length > 0) {
          setRoles(response);
        } else {
          toast.error('Rol bulunamadı');
        }
      } catch (error) {
        console.error('Roller yüklenirken hata:', error);
        toast.error('Roller yüklenirken bir hata oluştu');
      }
    };

    if (open) {
      void fetchRoles();
    }
  }, [open, userManagementStore]);

  const roleOptions = useMemo(
    () => roles.map(role => ({ value: role, label: role })),
    [roles]
  );

  const handleSubmit = async (values: UserEditFormValues) => {
    if (!selectedUser?.id) {
      return;
    }

    try {
      await userManagementStore.updateUser(selectedUser.id, values);
      onClose();
      toast.success('Kullanıcı başarıyla güncellendi');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Güncelleme sırasında bir hata oluştu');
    }
  };

  const handleClose = () => {
    onClose();
    userManagementStore.setSelectedUser(null);
  };

  if (!open || !selectedUser) {
    return null;
  }

  const initialValues: UserEditFormValues = {
    username: selectedUser.userName || '',
    email: selectedUser.email || '',
    role: selectedUser.roles?.[0] || '',
    isActive: selectedUser.isActive || false,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {() => (
        <Popup
          isOpen={open}
          onClose={handleClose}
          title="Kullanıcı Düzenle"
          description="Kullanıcı bilgilerini güncelleyin"
          variant="success"
          icon={<UserCog size={22} strokeWidth={2} />}
          footer={() => (
            <FormPopupFooter
              onClose={handleClose}
              saveLabel="Kaydet"
              submittingLabel="Kaydediliyor..."
            />
          )}
        >
          <Form className={styles.form}>
            <UserEditFormFields roleOptions={roleOptions} />
          </Form>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditUserModal);
